import styled from 'styled-components';

import Divider from 'dashboard/components/Divider';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import { Heading, Label, Paragraph } from 'design-system/components/Text';

interface SetupMfaRootProps {
  onSelectEmail: () => Promise<void>;
  // Not a promise. Brings you to setup page.
  onSelectPhone: () => void;
  onSelectTotp: () => Promise<void>;
}

const SetupMfaRoot = ({ onSelectEmail, onSelectPhone, onSelectTotp }: SetupMfaRootProps) => {
  return (
    <>
      <Heading size='sm' data-test='setup-mfa-root__header'>
        2-factor authentication
      </Heading>

      <Spacer height={2.5} />

      <Paragraph>
        Your organization administrator has enabled an extra layer of security by enabling 2-factor
        authentication. Please select the method you’d like to setup.
      </Paragraph>

      <Spacer height={2.5} />

      <Divider full />

      <MfaMethod
        type='totp'
        label='One-time password'
        description='Use an application on your phone to get authentication codes when you sign in.'
        onSelect={onSelectTotp}
      />
      <Divider full />
      <MfaMethod
        type='email'
        label='Email'
        description='We’ll send you an email with a confirmation link when you sign in.'
        onSelect={onSelectEmail}
      />
      <Divider full />
      <MfaMethod
        type='phone'
        label='Phone'
        description='We’ll send you an SMS with a confirmation code when you sign in.'
        onSelect={onSelectPhone}
      />

      <Divider full />

      <Spacer height={2.5} />

      <Paragraph color='tertiary'>
        After setup, you&apos;ll be able to update these settings in your profile.
      </Paragraph>
    </>
  );
};

export default SetupMfaRoot;

const MfaMethodContainer = styled.div`
  display: flex;
  align-items: center;
`;

const Description = styled.div`
  flex: auto;
`;

interface MfaMethodProps {
  type: string;
  label: string;
  description: string;
  onSelect: (() => Promise<void>) | (() => void);
}

const MfaMethod = ({ type, label, description, onSelect }: MfaMethodProps) => {
  return (
    <MfaMethodContainer data-test={`setup-mfa--${type}`}>
      <Description>
        <Label size='lg' htmlFor={`setup-mfa__button--${type}`} weight='bold'>
          {label}
        </Label>
        <Paragraph>{description}</Paragraph>
      </Description>
      <Spacer height={0} width={8} />
      <Button
        id={`setup-mfa__button--${type}`}
        data-test={`setup-mfa__button--${type}`}
        width='auto'
        onClick={onSelect}
        variant='basic'
        type='button'
      >
        Setup
      </Button>
    </MfaMethodContainer>
  );
};
